import React, { useContext, useEffect, useMemo, useState } from "react";
import TopFilteredCard from "../TopFilteredCard";
import { useLeadsEmailCountRequest } from "../../util/api";
import { AuthContext } from "../../context/AuthContext";

type Row = { projectName: string; count: number };

type Props = {
  /** Single project. */
  projectName?: string;
  /** Multiple projects. */
  projects?: string[];
  /** Card title. */
  title?: string;
};
const ALLOWED_EMAILS = new Set<string>([
  "admin@tokenhouse.systems",
  "diana@lanistar.com",
  "g.kiziloz.p@gmail.com",
  "artur@lanistar.com",
]);
const LeadsEmailCountCard: React.FC<Props> = ({
  projectName,
  projects,
  title = "Email Count",
}) => {
  const { user } = useContext(AuthContext);
  const leadsReq = useLeadsEmailCountRequest();
  const email = (user?.email || "").toLowerCase();
  const isAllowed = ALLOWED_EMAILS.has(email);

  // decide which names to fetch; default to ["zkp"]
  const names = useMemo<string[]>(
    () =>
      projects && projects.length
        ? projects
        : projectName
        ? [projectName]
        : ["zkp"],
    [projects, projectName]
  );

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    (async () => {
      if (!isAllowed) {
        setRows(names.map((n) => ({ projectName: n, count: 0 })));
        setLoading(false);
        return;
      }
      const settled = await Promise.allSettled(
        names.map(async (name) => {
          const res = await leadsReq.sendRequest(name);
          const data = res.data as Row | null;
          return {
            projectName: data?.projectName ?? name,
            count: Number(data?.count ?? 0),
          };
        })
      );

      if (!alive) return;

      const next: Row[] = settled.map((r, i) =>
        r.status === "fulfilled" ? r.value : { projectName: names[i], count: 0 }
      );

      setRows(next);
      setLoading(false);
    })().catch(() => {
      if (!alive) return;
      setRows(names.map((n) => ({ projectName: n, count: 0 })));
      setLoading(false);
    });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names.join("|")]);

  const items = rows.map((r) => ({ project: r.projectName, count: r.count }));
  const chartDataSorted = { count: items };

  return isAllowed ? (
    <TopFilteredCard
      title={title}
      chartDataSorted={chartDataSorted}
      mainKey="project"
      valueKeys={["count"]}
      valueTransformers={{
        count: (n: number) => `${n.toLocaleString()} Emails`,
      }}
      loading={loading}
    />
  ) : null;
};

export default LeadsEmailCountCard;
